import {
  MemberRoleTypes,
  PartialSdbMemberTypes,
  SdbMemberTypes,
} from "@src/custom-types/member-types";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";

/** Determines whether the member is in type of company */
export function isCompanyMember(
  member: SdbMemberTypes | FaroCoreApiTypes.LoggedInUser
): member is
  FaroCoreApiTypes.CompanyMemberBase | FaroCoreApiTypes.CompanyMemberDetails {
  return (
    "role" in member &&
    isCompanyRole(member.role) &&
    !("dateJoined" in member) &&
    !isLoggedInUser(member)
  );
}

/**
 * Determines whether the member is for company base or not
 * Based on the definition of sdbTeamMemberBase, it will also be returned as true
 */
export function isCompanyMemberBase(
  member: SdbMemberTypes | FaroCoreApiTypes.LoggedInUser
): member is FaroCoreApiTypes.CompanyMemberBase {
  return (
    isCompanyMember(member) &&
    !("userRole" in member) &&
    !isLoggedInUser(member)
  );
}

/** Determine whether the type of member is from company with details or not */
export function isCompanyMemberDetails(
  member: SdbMemberTypes | FaroCoreApiTypes.LoggedInUser
): member is FaroCoreApiTypes.CompanyMemberDetails {
  return (
    isCompanyMember(member) &&
    "userRole" in member &&
    isCompanyRole(member.userRole) &&
    !isLoggedInUser(member)
  );
}

/** Determines whether the member is for projects or not */
export function isProjectMemberBase(
  member: SdbMemberTypes | FaroCoreApiTypes.LoggedInUser
): member is FaroCoreApiTypes.ProjectMemberBase {
  return (
    "role" in member && isProjectRole(member.role) && !isLoggedInUser(member)
  );
}

/** Determines whether the member is in type of group */
export function isGroupMember(
  member: unknown
): member is FaroCoreApiTypes.GroupMember {
  return (
    typeof member === "object" &&
    member !== null &&
    "role" in member &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows role inputs
    isCompanyRole(member.role as any) &&
    !("userRole" in member) &&
    "dateJoined" in member
  );
}

/** Returns whether the member type is in type of UserDetails */
export function isUserDetails(
  user:
    | FaroCoreApiTypes.UserDetails
    | FaroCoreApiTypes.LoggedInUser
    | PartialSdbMemberTypes
    | FaroCoreApiTypes.GroupMember
): user is FaroCoreApiTypes.UserDetails {
  return (
    "haveCamera" in user && "howFound" in user && "profileImageUrl" in user
  );
}

/** Determines whether the member type is from user as manager or not */
export function isUserAsManager(
  member:
    | SdbMemberTypes
    | FaroCoreApiTypes.UserDetails
    | FaroCoreApiTypes.UserAsManager
    | FaroCoreApiTypes.LoggedInUser
): member is FaroCoreApiTypes.UserAsManager {
  return (
    !("role" in member) && !isUserDetails(member) && !isLoggedInUser(member)
  );
}

/** Whether the member is loggedInUser or not */
export function isLoggedInUser(
  user:
    | FaroCoreApiTypes.UserDetails
    | FaroCoreApiTypes.LoggedInUser
    | PartialSdbMemberTypes
    | FaroCoreApiTypes.GroupMember
): user is FaroCoreApiTypes.LoggedInUser {
  return "authenticationInfo" in user;
}

/** Determines whether the role is related to company or not */
export function isCompanyRole(
  role: MemberRoleTypes
): role is FaroCoreApiTypes.CompanyRole {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows AllCompanyRoles inputs
  return FaroCoreApiConst.allCompanyRoles.includes(role as any);
}

/** Determines whether the role is related to project or not */
export function isProjectRole(
  role: string
): role is FaroCoreApiTypes.ProjectRole {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- allows AllProjectRoles inputs
  return FaroCoreApiConst.allProjectRoles.includes(role as any);
}

// TODO: ST-646 Add new group viewer role: https://faro01.atlassian.net/browse/ST-3056
/** Determines whether the role is related to group or not */
export function isGroupRole(
  role: MemberRoleTypes
): role is FaroCoreApiTypes.GroupRole {
  return ["COMPANY_MANAGER", "GROUP_VIEWER", "PROJECT_MANAGER"].includes(role);
}
